<!-- Copyright 2023, Common Good Learning Tools LLC -->
<template>
	<div class="k-page-wrapper">
		<h1 class="k-page-title">
			<k-icon @click="return_to_admin_main" large color="primary" class="mr-2">fas fa-home</k-icon>
			Home Page Content
			<span v-if="subdomain_data">: {{subdomain_data.agency_name}}</span>
		</h1>

		<div v-if="initialized" style="max-width:1160px;border:1px solid #ccc; padding:2px;border-radius:8px;background-color:#fff;" class="mx-auto my-4">
			<k-tabs v-model="tab" bg-color="primary">
				<k-tab v-for="(item) in tabs" :key="item.key"><b>{{item.text}}</b></k-tab>
			</k-tabs>
			<k-tabs-items v-model="tab">
				<k-tab-item v-for="(item) in tabs" :key="item.key" class="pa-2"><div :class="editor_wrapper_class">
					<froala-wrapper :config="editor_config()" v-model="edited_home_page_content[item.key]" />
					<div class="text-right mt-4 mb-2"><k-btn color="primary" @click="save_changes(item.key)" :disabled="!content_changed[item.key]">
						<k-icon small class="mr-2">fas fa-save</k-icon> Save Changes</k-btn>
					</div>
				</div>
				</k-tab-item>
			</k-tabs-items>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'

export default {
	data() { return {
		tabs: [],
		tab: 'staff',
		edited_home_page_content: {},
	}},

	computed: {
		...mapState(['home_page_content', 'site_config', 'subdomain_data']),

		editor_wrapper_class() {
			// wrap the editor in the wrapper we'll use for the home page for the site; this makes, e.g., paragraphs the right max-width
			if (this.site_config.show_pop_up == 'yes') return 'k-home-dialog-wrapper-inner'
			else return 'k-home-home-wrapper-inner'
		},

		content_changed() {
			let o = {}
			for (let item of this.tabs) {
				o[item.key] = (this.edited_home_page_content[item.key] != this.home_page_content[item.key])
			}
			return o
		},
		
		initialized() {
			for (let item of this.tabs) if (!item.initialized) return false
			return true
		},
	},
	
	created() {
		vapp.admin_home_page_content = this

		let o = {}
		let tabs = []

		// if we're currently viewing a domain, and user is a domain admin (or a superuser), this tool just allows for managing the home page for the domain
		if (this.subdomain_data && vapp.has_admin_right('subdomain_admin')) {
			// we currenly only use the 'staff' home page for subdomains; the 'key' (aka home_page_variant) must have the following format (this is checked on the server too)
			let key = `staff_${this.subdomain_data.subdomain}`
			tabs.push({key: key, text: `Agency Home Page: ${this.subdomain_data.subdomain}`, initialized: false})
			o[key] = ''

		} else {
			// site_config determines which variants we support
			for (let key of this.site_config.home_page_variants) {
				if (key == 'staff') {
					// if we only use one home page, just call it 'home page'
					if (this.site_config.home_page_variants.length == 1) {
						tabs.push({key: 'staff', text: 'Home Page', initialized: false})
					// if we only have 'staff' and 'unsignedin', call this 'Signed In'
					} else if (this.site_config.home_page_variants.length == 2 && this.site_config.home_page_variants[1] == 'unsignedin') {
						tabs.push({key: 'staff', text: 'Signed In', initialized: false})
					// otherwise call it 'Staff'
					} else {
						tabs.push({key: 'staff', text: 'Staff', initialized: false})
					}
				} 
				else if (key == 'student') tabs.push({key: 'student', text: 'Students', initialized: false})
				else if (key == 'parent') tabs.push({key: 'parent', text: 'Parents (Family View)', initialized: false})
				else if (key == 'unsignedin') tabs.push({key: 'unsignedin', text: 'Not Signed In', initialized: false})
				else if (key == 'pop_up') tabs.push({key: 'pop_up', text: 'Pop Up Content', initialized: false})
				else if (key == 'terms_of_service') tabs.push({key: 'terms_of_service', text: 'Terms of Use', initialized: false})

				o[key] = ''
			}

			if (this.site_config.support_icon.enabled) {
				tabs.push({key: 'support_page', text: 'Support Page', initialized: false})
				o['support_page'] = ''
			}
		}

		this.edited_home_page_content = o
		this.tabs = tabs
	},

	mounted() {
		// retrieve the home_page_content for all pages
		for (let item of this.tabs) {
			this.$store.dispatch('get_home_page_content', item.key).then(() => {
				this.edited_home_page_content[item.key] = this.home_page_content[item.key]
				item.initialized = true
			})
		}
	},

	methods: {
		editor_config() {
			return U.get_froala_config({
				heightMin: 300,
			})
		},

		save_changes(key) {
			let payload = {
				user_id: this.$store.state.user_info.user_id,
				home_page_variant: key,
				home_page_content: U.encode_blocked_keywords(this.edited_home_page_content[key]),
				encode_blocked_keywords: ['home_page_content'],
				subdomain: this.subdomain_data?.subdomain,
			}
			
			U.loading_start()
			U.ajax('admin_save_home_content', payload, result=>{
				U.loading_stop()
				if (result.status != 'ok') {
					vapp.ping()		// call ping to check if the session is expired
					vapp.$alert('An error occurred when attempting to save the home page content.')
					return
				}
				this.$store.commit('set', [this.home_page_content, key, this.edited_home_page_content[key]])
			})
		},

		return_to_admin_main() {
			this.$router.push({ path: '/' })
		},

	}
}
</script>

<style lang="scss"></style>
